import {useCallback, useEffect, useRef, useState} from 'react';

const ACTION_CHANGE_REENTER_DELAY_MS = 500;

export type StickyState = 'hidden' | 'visible' | 'exiting';

export interface StickyTransition {
  state: StickyState;
  appliedIsBuyNow: boolean;
  handleAnimationEnd: () => void;
}

export const useStickyTransition = (isVisible: boolean, liveIsBuyNow: boolean): StickyTransition => {
  const [state, setState] = useState<StickyState>('hidden');
  const [appliedIsBuyNow, setAppliedIsBuyNow] = useState(liveIsBuyNow);

  const isReenteringRef = useRef(false);
  const reenterTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const liveIsBuyNowRef = useRef(liveIsBuyNow);
  liveIsBuyNowRef.current = liveIsBuyNow;
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;

  useEffect(() => () => clearTimeout(reenterTimeoutRef.current), []);

  useEffect(() => {
    if (isReenteringRef.current) {
      return;
    }

    if (isVisible && state === 'hidden') {
      setState('visible');
      return;
    }

    if (!isVisible && state === 'visible') {
      setState('exiting');
    }
  }, [isVisible, state]);

  useEffect(() => {
    if (liveIsBuyNow === appliedIsBuyNow || isReenteringRef.current) {
      return;
    }

    if (state === 'visible') {
      isReenteringRef.current = true;
      setState('exiting');
    } else {
      setAppliedIsBuyNow(liveIsBuyNow);
    }
  }, [liveIsBuyNow, appliedIsBuyNow, state]);

  const handleAnimationEnd = useCallback(() => {
    if (state !== 'exiting') {
      return;
    }

    setState('hidden');

    if (isReenteringRef.current) {
      reenterTimeoutRef.current = setTimeout(() => {
        setAppliedIsBuyNow(liveIsBuyNowRef.current);
        isReenteringRef.current = false;
        if (isVisibleRef.current) {
          setState('visible');
        }
      }, ACTION_CHANGE_REENTER_DELAY_MS);
    }
  }, [state]);

  return {state, appliedIsBuyNow, handleAnimationEnd};
};
